import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/app/api";

export default function EntitlementDetail() {
  const { entitlementId } = useParams();
  const [record, setRecord] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/admin/entitlements/${entitlementId}`).then((r) => setRecord(r.data))
    .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Entitlement not found")), [entitlementId]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body, message) => {
    setBusy(true);
    try {
      await api.post(`/admin/entitlements/${entitlementId}/${path}`, body || {});
      if (message) toast.success(message);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <p className="rounded-2xl bg-rose-50 p-4 text-sm font-semibold text-rose-700" data-testid="entitlement-error">{error}</p>;
  if (!record) return <p className="text-sm text-slatey">Loading…</p>;

  const rule = record.rule_snapshot || {};

  return (
    <div data-testid="admin-entitlement-detail" className="space-y-6">
      <Link to="/app/admin/entitlements" className="inline-flex items-center gap-2 text-sm font-bold text-slatey hover:text-cobalt"><ArrowLeft className="h-4 w-4" /> All entitlements</Link>

      <div className="rounded-3xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-ink" data-testid="entitlement-learner">
              {record.learner_name} {record.is_demo && <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700">TEST</span>}
            </h1>
            <p className="mt-1 text-sm text-slatey">{record.opportunity_title} · {record.provider_name} · {record.intake_label}</p>
            <p className="mt-1 text-xs text-slatey">{record.application_reference} · config v{record.config_version} · {record.duration_months} months</p>
          </div>
          <span className="rounded-full bg-cobalt/10 px-4 py-2 text-sm font-bold capitalize text-cobalt" data-testid="entitlement-status">{record.status}</span>
        </div>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="entitlement-actions">
          {record.status === "eligible" && (
            <button onClick={() => act("provision", {}, "Provisioned at the MyAE integration boundary (simulated).")} disabled={busy} data-testid="entitlement-provision"
              className="rounded-full bg-cobalt px-4 py-2 text-xs font-bold text-white disabled:opacity-50">Provision benefit</button>
          )}
          {record.status === "provisioned" && (
            <button onClick={() => act("activate", null, "Benefit activated.")} disabled={busy} data-testid="entitlement-activate"
              className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white disabled:opacity-50">Activate benefit</button>
          )}
          <button onClick={() => act("recheck", null, "Rechecked against current commission state.")} disabled={busy} data-testid="entitlement-recheck"
            className="rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-ink disabled:opacity-50">Recheck qualification</button>
          {!["void", "expired"].includes(record.status) && (
            <button onClick={() => { const reason = window.prompt("Reason for voiding this entitlement?"); if (reason?.trim().length >= 3) act("void", { reason }, "Entitlement voided."); }}
              disabled={busy} data-testid="entitlement-void"
              className="rounded-full border border-rose-200 px-4 py-2 text-xs font-bold text-rose-600 disabled:opacity-50">Void</button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-5">
          <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="entitlement-qualification">
            <h2 className="font-display text-xl font-bold text-ink">Qualification</h2>
            <dl className="mt-3 space-y-2 text-sm">
              {[["Outcome", (record.outcome || "—").replaceAll("_", " ")],
                ["Qualifying event", (rule.qualifying_outcome_event || "—").replaceAll("_", " ")],
                ["Required commission status", (rule.required_commission_status || "—").replaceAll("_", " ")],
                ["Commission record", record.commission_id || "none yet"],
                ["Commission status at qualification", (record.commission_status_at_qualification || "—").replaceAll("_", " ")],
                ["Commission status now", (record.commission || {}).status || "—"],
                ["Eligible at", record.eligible_at?.slice(0, 16).replace("T", " ") || "—"],
                ["Provisioned", record.provisioning ? `${record.provisioning.method} · ${record.provisioning.reference}` : "—"],
                ["Active from", record.active_from?.slice(0, 10) || "—"],
                ["Expires", record.expires_at?.slice(0, 10) || "—"],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-4">
                  <dt className="text-slatey">{label}</dt>
                  <dd className="text-right font-semibold text-ink" data-testid={`qualification-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{value}</dd>
                </div>
              ))}
            </dl>
            {record.void && <p className="mt-3 rounded-2xl bg-rose-50 p-3 text-xs font-semibold text-rose-700" data-testid="entitlement-void-reason">Voided by {record.void.by}: {record.void.reason}</p>}
          </section>
        </div>

        <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="entitlement-events">
          <h2 className="font-display text-xl font-bold text-ink">Audit trail</h2>
          <ol className="mt-3 space-y-3">
            {(record.events || []).slice().reverse().map((event, index) => (
              <li key={index} className="flex gap-3 text-sm" data-testid={`entitlement-event-${index}`}>
                <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-cobalt" />
                <div>
                  <p className="font-semibold capitalize text-ink">{event.action.replaceAll("_", " ")}</p>
                  {event.detail && <p className="text-xs leading-5 text-slatey">{event.detail}</p>}
                  <p className="text-xs text-slatey">{event.by} · {event.at?.slice(0, 16).replace("T", " ")}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  );
}
